/**
 * Hybrid PartnerLogo Component
 * Automatically chooses between Tailwind and styled-components based on props
 */

import React from "react";
import type { TypePartnerLogoProps } from "./PartnerLogoTypes";
import Container from "./styles"; // Styled-components version
import { PartnerLogoTailwind } from "./PartnerLogoTailwind"; // Tailwind version
import { usePartnerLogoModel } from "./usePartnerLogoModel";
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";

const PartnerLogoHybrid = React.forwardRef<
  HTMLSpanElement,
  TypePartnerLogoProps
>((props, ref) => {
  const {
    partnerName,
    backgroundType,
    logoType = "symbol",
    height,
    width,
    size,
    "aria-label": ariaLabel,
    svgProps,
    ...rest
  } = props;

  const model = usePartnerLogoModel({
    partnerName,
    backgroundType,
    logoType,
    height,
    width,
    size,
    "aria-label": ariaLabel,
  });

  // PartnerLogo declares its own height/width/size sizing props, so they are
  // destructured out above before checking hasStyledProps(rest) — a plain
  // <PartnerLogo height="30px" /> routes to Tailwind. Only genuine system props
  // (mr, display, color, ...) force the styled-components path; a styled(PartnerLogo)
  // extension routes to Tailwind and still applies its styles via the forwarded className.
  if (hasStyledProps(rest)) {
    return (
      <Container
        height={model.resolvedHeight}
        width={model.resolvedWidth}
        logoSize={model.logoSizeKey}
        className="logo"
        data-qa-logo={partnerName}
        ref={ref}
        {...rest}
        {...model.containerAriaProps}
      >
        <svg
          className="logo-svg"
          viewBox={model.logoViewBox}
          focusable={false}
          data-qa-logo-svg={`${partnerName}-svg`}
          {...svgProps}
          {...model.svgAriaProps}
        >
          <use
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xlinkHref={`#seeds-svgs_${model.logo}`}
          />
        </svg>
      </Container>
    );
  }

  // Use Tailwind version (preferred - better performance)
  return <PartnerLogoTailwind {...props} ref={ref} />;
});

PartnerLogoHybrid.displayName = "PartnerLogo";
export default PartnerLogoHybrid;
